.field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
}

/* ── Label ── */
.label {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.125rem;
  color: var(--text-secondary);
}
.required {
  color: var(--error-solid);
}
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

/* ── Field box: a horizontal group of adjacent bordered cells ──
   The input cell carries the border + focus ring; attached addon segments are
   separate bordered boxes that stay OUTSIDE the ring (Figma segmented model). */
.box {
  display: flex;
  align-items: stretch;
  width: 100%;
  border-radius: 0.5rem;
  background: var(--surface);
  box-shadow: var(--shadow-xs);
}
.box.disabled {
  cursor: not-allowed;
}

/* ── Input cell (text input + inline affixes/icons) ── */
.inputCell {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--surface);
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease;
}
.md .inputCell {
  padding: 0.625rem 0.875rem;
}
.sm .inputCell {
  padding: 0.5rem 0.875rem;
}

/* The ring lifts the cell above its neighbours so the brand border paints over the seam. */
.inputCell:focus-within {
  z-index: 2;
  border-color: var(--brand-solid);
  box-shadow: var(--focus-ring);
}

.box.error .inputCell {
  border-color: var(--error-solid);
}
.box.error .inputCell:focus-within {
  box-shadow: var(--focus-ring-error);
}
.box.disabled .inputCell {
  background: var(--surface-secondary);
}

/* Square off the cell corners that meet an addon segment. */
.box.hasLeading .inputCell {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.box.hasTrailing .inputCell {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

/* ── Attached addon segments (separate boxes, outside the ring) ──
   A control addon (e.g. a Button) fills the whole segment — no padding around it — so
   the entire segment is its hit/hover area and it never makes the field taller than a
   plain input. A text addon (a string, e.g. `http://`) gets its own padding instead. */
.segment {
  position: relative;
  z-index: 0;
  display: flex;
  flex-shrink: 0;
  align-items: stretch;
  border: 1px solid var(--border);
  background: var(--surface);
  overflow: hidden;
}
.segmentPadded {
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.875rem;
  color: var(--text-tertiary);
  font-family: var(--font-family-base);
  font-size: var(--text-md-font-size);
  line-height: var(--text-md-line-height);
  white-space: nowrap;
}
/* Overlap the shared edge so the double border collapses into one divider. */
.segmentLeading {
  border-top-left-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
  margin-right: -1px;
}
.segmentTrailing {
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  margin-left: -1px;
}
.box.error .segment {
  border-color: var(--error-solid);
}
.box.disabled .segment {
  background: var(--surface-secondary);
}

/* ── Inline affixes (prefix / suffix) ── */
.affix {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-tertiary);
  font-family: var(--font-family-base);
  font-size: var(--text-md-font-size);
  line-height: var(--text-md-line-height);
  white-space: nowrap;
}

.icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  color: var(--text-quaternary);
}

/*
 * The in-field alert glyph the error state adds — see `resolvedTrailingIcon`.
 *
 * It sets `--icon-color`, not just `color`: the `Icon` primitive resolves its colour as
 * `var(--icon-color, currentColor)`, and `apps/admin` pins `--icon-color` to `--gray-500`
 * document-wide (HS-1184). Setting only `color` would leave the glyph grey there while it turned
 * red in the Portal — the same component telling two different stories about the same state.
 */
.errorIcon {
  --icon-color: var(--error-solid);

  color: var(--error-solid);
}

/* ── Native input ── */
.input {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-primary);
  font-family: var(--font-family-base);
  font-size: var(--text-md-font-size);
  line-height: var(--text-md-line-height);
}
.input:focus {
  outline: none;
}
.input::placeholder {
  color: var(--text-quaternary);
}
.input:disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}

/* ── Hint / error message ── */
.hint {
  color: var(--text-tertiary);
}
.errorText {
  color: var(--error-solid);
}

/* Windows High Contrast — keep the focus indication visible. */
@media (forced-colors: active) {
  .inputCell:focus-within {
    outline: 2px solid Highlight;
    outline-offset: 2px;
  }
}
